import { FunctionComponent } from 'react';
import { YourDetailsTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { useUserData } from '@hooks';
import {
  msalInstance,
  handleLoginPageEvent,
  yourDetailsSchema,
  parseZodErrors,
  LoginRequestConfig,
  handleLoginRequest,
} from '@utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { ZodError } from 'zod';
import { useEVChargerStore } from '../../../../hooks/useEVChargerStore';
import { useHandleSubmit } from '../../../../hooks/useHandleSubmit';
import { EVChargerPayload } from '../payload/Payload';

export const YourDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const payload = EVChargerPayload();
  const { mutateAsync, isPending } = useHandleSubmit();
  const { update, data } = useEVChargerStore();

  const formData = {
    firstName: data.userDetails.firstName,
    lastName: data.userDetails.lastName,
    email: data.userDetails.email,
  };
  const [crmId, setCrmId] = useState<string>('');
  const [idProvider, setIdProvider] = useState<string>('');
  const { isLoggedInUser, contactData, isLoading, isFetching } =
    useUserData(crmId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const isLoadingUserData = isLoading || isFetching;

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }
    if (contactData) {
      const data = {
        email: contactData.emailaddress1 || '',
        firstName: contactData.firstname || '',
        lastName: contactData.lastname || '',
      };
      update('userDetails.firstName', data.firstName);
      update('userDetails.lastName', data.lastName);
      update('userDetails.email', data.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactData, crmId, idProvider]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    if (name === 'firstName') {
      update('userDetails.firstName', value);
    } else if (name === 'lastName') {
      update('userDetails.lastName', value);
    } else if (name === 'email') {
      update('userDetails.email', value);
    }
    setErrorMessages([]);
  };

  const handleSubmit = async () => {
    const { firstName, lastName, email } = data.userDetails;
    try {
      yourDetailsSchema.parse({ firstName, lastName, email });

      await mutateAsync(payload);
      navigate(RoutesList.CONFIRMATION);
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrorMessages = parseZodErrors(error);
        setErrorMessages(newErrorMessages);
      }
    }
  };

  const loginConfig: LoginRequestConfig = {
    msal: msalInstance,
    navigate: () => navigate(RoutesList.YOUR_DETAILS, { replace: true }),
  };

  const handleLogin = () => handleLoginRequest(loginConfig);

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.EMAIL_CONFIRMATION);
    },
  };

  const buttonNext = {
    label: 'Submit',
    loading: isPending,
    onClick: handleSubmit,
  };

  return (
    <YourDetailsTemplate
      isLoadingUserData={isLoadingUserData}
      isLoggedInUser={isLoggedInUser}
      userData={formData}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorMessages={errorMessages}
      handleLogin={handleLogin}
      onChange={handleChange}
      idProvider={idProvider}
    />
  );
};
